import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CanonicalTopicGroupedBy, CanonicalTopicGroupedByEnum } from "schema"

type Props = {
  value: CanonicalTopicGroupedBy
  onChange: (value: CanonicalTopicGroupedBy) => void
}

export const GroupBySelector = ({ value, onChange }: Props) => {
  const { t } = useTranslation()

  const { data: segmentGroups } = useSegmentGroupsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()

  const segmentGroupsById = useMemo(() => {
    return new Map(segmentGroups?.map((segmentGroup) => [segmentGroup.id, segmentGroup]))
  }, [segmentGroups])

  const selectGroupValue = useMemo(() => {
    if (value.type === CanonicalTopicGroupedByEnum.company) {
      return {
        placeholder: t("dashboard.groupedBy.label"),
        value: value.type,
      }
    }

    if (value.type === CanonicalTopicGroupedByEnum.segmentGroup) {
      const segmentGroup = segmentGroupsById.get(value.segmentGroupId)

      return {
        placeholder: segmentGroup
          ? getSegmentGroupTranslation(segmentGroup)
          : t(`dashboard.groupedBy.${value.type}`),
        value: `${value.type}-${value.segmentGroupId}`,
      }
    }

    return {
      placeholder: t(`dashboard.groupedBy.${value.type}`),
      value: value.type,
    }
  }, [value, t, segmentGroupsById, getSegmentGroupTranslation])

  return (
    <Select
      value={selectGroupValue.value}
      onValueChange={(selectedValue) => {
        const [type, id] = selectedValue.split("-")

        switch (type) {
          case CanonicalTopicGroupedByEnum.segmentGroup:
            if (!id) {
              onChange({ type: CanonicalTopicGroupedByEnum.company })
              break
            }

            onChange({ type: CanonicalTopicGroupedByEnum.segmentGroup, segmentGroupId: id })
            break
          case CanonicalTopicGroupedByEnum.campaign:
            onChange({ type: CanonicalTopicGroupedByEnum.campaign })
            break
          default:
            onChange({ type: CanonicalTopicGroupedByEnum.company })
        }
      }}
    >
      <SelectTrigger
        className={cn(
          "w-auto min-w-44",
          selectGroupValue.value === "none" && "text-muted-foreground",
        )}
      >
        <SelectValue>{selectGroupValue.placeholder}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel className="text-xs font-normal text-muted-foreground">
            {t("dashboard.groupedBy.label")}
          </SelectLabel>
          <SelectItem value={CanonicalTopicGroupedByEnum.company} className="text-muted-foreground">
            {t("dashboard.groupedBy.none")}
          </SelectItem>
          <SelectLabel>{t("dashboard.groupedBy.segmentGroup")}</SelectLabel>
          {segmentGroups?.map((segmentGroup) => (
            <SelectItem
              key={segmentGroup.id}
              value={`${CanonicalTopicGroupedByEnum.segmentGroup}-${segmentGroup.id}`}
              className="pl-4"
            >
              {getSegmentGroupTranslation(segmentGroup)}
            </SelectItem>
          ))}
          <SelectItem value={CanonicalTopicGroupedByEnum.campaign}>
            {t("dashboard.groupedBy.campaign")}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
